import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { StatementItem } from '@webapp/modules/accounts-payable/entities/StatementItem'
import { statementItemQueries } from '@webapp/modules/accounts-payable/query-configs/statementItemQueries'

interface IUseQueryUnmatchedStatementItemsParams {
  accountId: number
  /** Suggested-match filter — sibling CC item id (BHV-037) */
  matchCreditcardItemId?: Nullable<number>
  onError?: (err: unknown) => void
}

interface IUseQueryUnmatchedStatementItemsReturn {
  items: StatementItem[]
  /** metadata.summary.total_amount — pane balance exposure */
  totalAmount: Nullable<number>
  /** pagination.count — FS tab badge (BHV-030) */
  unmatchedCount: number
  isLoading: boolean
  error: unknown
}

export const useQueryUnmatchedStatementItems = ({
  accountId,
  matchCreditcardItemId = null,
  onError,
}: IUseQueryUnmatchedStatementItemsParams): IUseQueryUnmatchedStatementItemsReturn => {
  const { params, updateNumPages } = usePagination({
    id: 'ap-reconcile-unmatched-statement-items',
  })

  const { data, isLoading, error } = useQuery({
    ...statementItemQueries.unmatched({
      creditcardId: accountId,
      orderBy: params.orderBy,
      search: params.search,
      page: params.page,
      pageSize: params.pageSize,
      ...(matchCreditcardItemId !== null ? { matchCreditcardItemId } : {}),
    }),
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
    onError,
  })

  const items = useMemo(
    () =>
      (data?.items ?? []).map((constructor) => new StatementItem(constructor)),
    [data]
  )

  return {
    items,
    totalAmount: data?.totalAmount ?? null,
    unmatchedCount: data?.pagination.count ?? 0,
    isLoading,
    error,
  }
}
